import { useMemo, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { defer } from 'lodash';

import { useTrackEvent } from '@/lib/analytics';
import { useKeyPress } from '@/lib/hooks/use-key-press';
import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { ShortcutIndicator } from '@/components/shortcut-indicator';
import { AutoResizeTextArea } from '@/components/form/text-area/auto-resize-text-area';
import { notNil } from '@/lib/utils';
import { BuildType } from '@/core/exploration';
import { ChatMode, Conversation } from '@/explore/types';

import { useExplorationUIContext } from '../exploration-ui-context';
import { Exploration } from '../../types';
import { AddButton } from './buttons/add-button';
import { AddDropdown } from './buttons/add-dropdown';
import { ChatSettings } from '../chat-cell/components/chat-settings';
import { useChatSettings } from '../chat-cell/use-chat-settings';

import styles from './cell-input.module.scss';

export type ActionTypes = BuildType & 'data';

interface CellInputProps {
  emptyExploration?: boolean;
  onAsk: (
    question: string,
    sources: string[],
    mode: ChatMode,
  ) => Promise<{ conversation: Conversation | null; error: Error | undefined }>;
  onClose: () => void;
  onShowCatalog: (query: string) => void;
  onClickExploration: (exploration: Exploration) => void;
}

export const CellInput = (props: CellInputProps) => {
  const { emptyExploration = false, onAsk, onClose, onShowCatalog, onClickExploration } = props;
  const { conversations, setConversationDraft } = useExplorationUIContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputHasFocus, setInputHasFocus] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const trackEvent = useTrackEvent();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const { selectedSources, selectedMode, setSettings } = useChatSettings();
  const trimmedSearchTerm = useMemo(() => searchTerm.trim(), [searchTerm]);

  const handleClose = () => {
    trackEvent('CellInput closed');
    onClose();
  };

  useEffect(() => {
    const draft = conversations.new?.draft;
    if (!trimmedSearchTerm.length && notNil(draft)) {
      setSearchTerm(draft);
      setConversationDraft('new', null);
    }
  }, [conversations, setConversationDraft, trimmedSearchTerm.length]);

  useKeyPress(
    'Escape',
    (e) => {
      e.preventDefault();
      handleClose();
    },
    { targetRef: inputRef },
  );

  useKeyPress(
    ['o', 'ctrl', 'meta'],
    (e) => {
      e.preventDefault();
      handleShowCatalog();
    },
    { includeInputs: true },
  );

  const handleShowCatalog = () => {
    onShowCatalog(trimmedSearchTerm);
  };

  const handleQuestion = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (searchTerm.length && selectedSources.length > 0 && !loading) {
      if (conversations.new?.draft !== null) {
        setConversationDraft('new', null);
      }
      setSettings({ sources: selectedSources, mode: selectedMode });
      setLoading(true);
      onAsk(searchTerm, selectedSources, selectedMode);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuestion();
    } else if (e.key === 'ArrowDown') {
      const target = e.currentTarget;
      const isAtEnd = target.selectionStart === target.value.length;

      if (trimmedSearchTerm.length === 0 || isAtEnd) {
        e.preventDefault();
        setIsSettingsOpen(true);
      }
    }
  };

  const handleSearchTermChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.currentTarget.value;
    setSearchTerm(value);
  };

  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
    defer(() => inputRef.current?.focus());
  };

  return (
    <>
      {emptyExploration && <div className={styles.title}>Let&apos;s dive in</div>}
      <div className={classNames(styles.wrapper, emptyExploration && styles.revealForm)}>
        <div className={styles.toolsRow}>
          <Tooltip
            hasArrow={false}
            content={
              <span>
                Browse your explorations and models{' '}
                <ShortcutIndicator shortcut={['cmd', 'O']} size="small" />
              </span>
            }>
            <Button
              icon={<Icon name="Model" size={16} />}
              variant="secondary"
              size="small"
              onMouseUp={(e) => e.currentTarget.blur()}
              onClick={handleShowCatalog}>
              Data catalog
            </Button>
          </Tooltip>
          <AddButton size="small" type="variable" onClick={onClickExploration} />
          <AddButton size="small" type="text" onClick={onClickExploration} />
          <AddDropdown type="code" onClick={onClickExploration} />
          <AddDropdown type="templates" onClick={onClickExploration} />
        </div>
        <form className={styles.form} onSubmit={handleQuestion}>
          <div className={styles.inputRow}>
            <AutoResizeTextArea
              value={searchTerm}
              onChange={handleSearchTermChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setInputHasFocus(true)}
              onBlur={() => setInputHasFocus(false)}
              placeholder="Type to ask AI a question"
              autoFocus
              rows={1}
              ref={inputRef}
              className={styles.input}
            />
            <Button
              type="submit"
              variant="primary"
              size="compact"
              disabled={trimmedSearchTerm.length === 0 || selectedSources.length === 0 || loading}
              className={styles.submitBtn}>
              <Icon name="ArrowRight" size={16} />
            </Button>
          </div>

          <div className={classNames([styles.inputActions])}>
            <ChatSettings
              selectedSources={selectedSources}
              selectedMode={selectedMode}
              size="small"
              onChange={setSettings}
              onClose={handleSettingsClose}
              isOpen={isSettingsOpen}
              onOpenChange={setIsSettingsOpen}
            />

            {trimmedSearchTerm.length > 0 && (
              <>
                <button type="button" className={styles.hint} onClick={handleShowCatalog}>
                  Search data catalog (
                  <ShortcutIndicator
                    shortcut={['cmd', 'o']}
                    size="tiny"
                    inline
                    className={styles.inlineShortcut}
                  />
                  )
                </button>
              </>
            )}
            {!emptyExploration ? (
              <button
                type="button"
                className={classNames(styles.hint, styles.cancelBtn)}
                onClick={handleClose}>
                {inputHasFocus ? 'Esc to close' : 'Click to close'}
              </button>
            ) : trimmedSearchTerm.length === 0 ? (
              <span className={styles.hint}>
                Open up an exploration or data model from your data catalog, or ask Al a question
              </span>
            ) : null}
          </div>
        </form>
      </div>
    </>
  );
};
